import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, BookOpen, Calculator, Globe2, ArrowRight, Lock } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { DjayAvatar, DjayBubble, DjayMeesterLogo, getDjayWelcomeMessage } from '../../mascot';
/**
 * WelcomeScreen — The first thing users see. Redesigned to feel like
 * the beginning of an adventure, not a SaaS landing page.
 *
 * Design principles:
 * - Student-first: "Start je avontuur" is the hero CTA
 * - Djay as guide: The mascot introduces itself and invites you in
 * - Warm palette: Orange/amber tones matching Djay's personality
 * - Playful motion: Floating decorative elements, bouncing mascot
 */
interface WelcomeScreenProps {
  onLoadDemo: () => void;
  onLogin: () => void;
  onRegister: () => void;
}
/** Floating decorative icon that drifts gently */
function FloatingIcon({
  children,
  delay,
  x,
  y
}: {
  children: React.ReactNode;
  delay: number;
  x: string;
  y: string;
}) {
  return <motion.div className="absolute text-orange-200/40 pointer-events-none select-none" style={{
    left: x,
    top: y
  }} initial={{
    opacity: 0,
    scale: 0.5
  }} animate={{
    opacity: [0, 0.6, 0.4, 0.6],
    scale: [0.5, 1, 0.95, 1],
    y: [0, -12, 0, -8, 0],
    rotate: [0, 5, -3, 5, 0]
  }} transition={{
    duration: 8,
    delay,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-688">
      {children}
    </motion.div>;
}
export function WelcomeScreen({
  onLoadDemo,
  onLogin,
  onRegister
}: WelcomeScreenProps) {
  const welcomeMsg = getDjayWelcomeMessage();
  return <div className="min-h-screen bg-gradient-to-b from-orange-50 via-amber-50/50 to-white flex flex-col items-center justify-center p-4 sm:p-8 relative overflow-hidden" data-id="element-689">
      {/* Ambient floating icons */}
      <FloatingIcon delay={0} x="8%" y="15%" data-id="element-690">
        <Calculator className="w-8 h-8 sm:w-10 sm:h-10" data-id="element-691" />
      </FloatingIcon>
      <FloatingIcon delay={1.5} x="85%" y="12%" data-id="element-692">
        <BookOpen className="w-7 h-7 sm:w-9 sm:h-9" data-id="element-693" />
      </FloatingIcon>
      <FloatingIcon delay={0.8} x="12%" y="75%" data-id="element-694">
        <Globe2 className="w-6 h-6 sm:w-8 sm:h-8" data-id="element-695" />
      </FloatingIcon>
      <FloatingIcon delay={2.2} x="80%" y="70%" data-id="element-696">
        <Sparkles className="w-7 h-7 sm:w-9 sm:h-9" data-id="element-697" />
      </FloatingIcon>
      <FloatingIcon delay={1} x="50%" y="8%" data-id="element-698">
        <span className="text-2xl sm:text-3xl font-black" data-id="element-699">π</span>
      </FloatingIcon>
      <FloatingIcon delay={2.8} x="92%" y="45%" data-id="element-700">
        <span className="text-xl sm:text-2xl font-black" data-id="element-701">abc</span>
      </FloatingIcon>

      {/* Main content */}
      <div className="w-full max-w-md relative z-10" data-id="element-702">
        {/* Logo */}
        <motion.div className="flex justify-center mb-6" initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-703">
          <DjayMeesterLogo size="lg" data-id="element-704" />
        </motion.div>

        {/* Djay hero section */}
        <motion.div className="flex flex-col items-center mb-6" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.15
      }} data-id="element-705">
          <motion.div animate={{
          y: [0, -10, 0]
        }} transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-706">
            <DjayAvatar size="hero" emotion={welcomeMsg.emotion} animate={true} data-id="element-707" />
          </motion.div>

          <motion.div className="mt-4 w-full max-w-[280px]" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.4
        }} data-id="element-708">
            <DjayBubble message={welcomeMsg.text} position="top" autoDismissMs={0} colorScheme="amber" data-id="element-709" />
          </motion.div>
        </motion.div>

        {/* Tagline */}
        <motion.div className="text-center mb-6" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35,
        duration: 0.4
      }} data-id="element-710">
          <h1 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight mb-2" data-id="element-711">
            Leren wordt een avontuur!
          </h1>
          <p className="text-base text-slate-500 font-medium max-w-xs mx-auto" data-id="element-712">
            Oefen rekenen, taal, spelling en meer — op jouw eigen niveau.
          </p>
        </motion.div>

        {/* CTA Stack */}
        <motion.div className="space-y-3" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.4
      }} data-id="element-713">
          {/* Primary: Register (student-first) */}
          <div className="relative group" data-id="element-714">
            <div className="absolute -inset-1 bg-gradient-to-r from-orange-400 to-amber-400 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-300" data-id="element-715"></div>
            <Button variant="primary" size="xl" className="relative w-full" onClick={onRegister} data-id="element-716">
              Start je avontuur
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-717" />
            </Button>
          </div>

          {/* Secondary: Try demo */}
          <Button variant="outline" size="lg" className="w-full !border-orange-200 !text-orange-700 hover:!bg-orange-50 hover:!border-orange-300" onClick={onLoadDemo} data-id="element-718">
            <Sparkles className="w-4 h-4 mr-2" data-id="element-719" />
            Eerst even uitproberen
          </Button>

          {/* Tertiary: Login */}
          <div className="pt-2 text-center" data-id="element-720">
            <p className="text-sm text-slate-500 font-medium" data-id="element-721">
              Heb je al een account?{' '}
              <button onClick={onLogin} className="text-orange-600 font-bold hover:text-orange-800 hover:underline transition-colors" data-id="element-722">
                Log in
              </button>
            </p>
          </div>
        </motion.div>

        {/* Trust footer */}
        <motion.div className="text-center mt-8 pb-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8
      }} data-id="element-723">
          <p className="text-xs text-slate-500 font-medium flex items-center justify-center gap-1.5 bg-white/50 py-2 px-4 rounded-full inline-flex border border-slate-100" data-id="element-724">
            <Lock className="w-3.5 h-3.5 text-slate-400" data-id="element-725" />
            Veilig — alle gegevens worden lokaal opgeslagen
          </p>
        </motion.div>
      </div>
    </div>;
}